// src/features/roles/queries.ts
import {
  useQuery,
  useQueryClient,
  type QueryKey,
  keepPreviousData,
} from "@tanstack/react-query";

import {
  listRoles,
  getRole,
  getRoleGrants,
  listPermissions,
} from "./api";

import { RolesKeys } from "./types";
import type { RoleDTO } from "./types";

/* =========================
 * ROLES — Read Hooks (Queries Only)
 * ========================= */

/** 🔹 List roles with pagination + filters
 *  NOTE: Backend only returns active / non-deleted roles.
 *  No status filter is sent from the UI.
 */
export function useRoles(params: RoleDTO.ListParams = {}) {
  const key = RolesKeys.list(params);
  return useQuery({
    queryKey: key,
    queryFn: () => listRoles(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/** 🔹 Get a single role by id */
export function useRole(id: number | null | undefined) {
  const enabled = typeof id === "number";
  return useQuery({
    queryKey: enabled
      ? RolesKeys.detail(id as number)
      : (["roles", "detail", "disabled"] as QueryKey),
    queryFn: () => getRole(id as number),
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/** 🔹 Role grants (permissions) — read-only */
export function useRoleGrants(roleId: number | null | undefined) {
  const enabled = typeof roleId === "number";
  return useQuery({
    queryKey: enabled
      ? RolesKeys.grants(roleId as number)
      : (["roles", "grants", "disabled"] as QueryKey),
    queryFn: () => getRoleGrants(roleId as number),
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/** 🔹 Global permissions catalog */
export function usePermissions() {
  return useQuery({
    queryKey: ["roles", "permissions"],
    queryFn: () => listPermissions(),
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });
}

/* =========================
 * Prefetch helpers (optional)
 * ========================= */

export function prefetchRoles(
  qc: ReturnType<typeof useQueryClient>,
  params: RoleDTO.ListParams = {}
) {
  return qc.prefetchQuery({
    queryKey: RolesKeys.list(params),
    queryFn: () => listRoles(params),
    staleTime: 30_000,
  });
}

export function prefetchRole(
  qc: ReturnType<typeof useQueryClient>,
  id: number
) {
  return qc.prefetchQuery({
    queryKey: RolesKeys.detail(id),
    queryFn: () => getRole(id),
    staleTime: 60_000,
  });
}

export function prefetchRoleGrants(
  qc: ReturnType<typeof useQueryClient>,
  roleId: number
) {
  return qc.prefetchQuery({
    queryKey: RolesKeys.grants(roleId),
    queryFn: () => getRoleGrants(roleId),
    staleTime: 60_000,
  });
}

/* =========================
 * SELECT-friendly helper for forms (read only)
 * ========================= */

/**
 * Provides string options for basic <Select> components while exposing full Role items.
 * - Backend already filters to active roles, so there is no status logic here.
 */
export function useRoleOptions(
  params: Partial<RoleDTO.ListParams> & { pageSize?: number } = {}
) {
  const pageSize = params.pageSize ?? 100;

  const queryParams: RoleDTO.ListParams = {
    page: params.page ?? 1,
    pageSize,
    q: params.q,
  };

  const { data, isLoading, isError, refetch } = useRoles(queryParams);
  const items = data?.items ?? [];

  const idToName = new Map<number, string>();
  const nameToId = new Map<string, number>();
  for (const r of items) {
    idToName.set(r.id, r.name);
    nameToId.set(r.name, r.id);
  }

  const options = isLoading
    ? ["Loading roles…"]
    : isError
    ? ["Failed to load roles"]
    : items.length
    ? items.map((r) => r.name)
    : ["No roles"];

  const disabled = isLoading || isError || items.length === 0;

  return {
    options,
    items,
    isLoading,
    isError,
    disabled,
    refetch,
    idToName,
    nameToId,
  };
}
